// type-only, so this module pulls in nothing at runtime and the styling
// module can depend on it without a cycle
import type { RouteLineStyle } from "./routeLineStyle";
import type { RouteRenderMode } from "./resolveRoutePath";

// Route views stack: ticking point-to-point and snap-to-road draws both, so
// the raw GPS line and the road-matched line can be read against each other.
// Where they diverge is where snap-to-road guessed or the fixes drifted.
//
// The renderer draws one line per trip, so it carries one view and the rest
// are drawn as comparison lines over the top (see routeOverlayLines). This
// decides which is which, in one precedence order - geometry and styling
// used to resolve that separately, and in opposite directions.

export type RouteViewsEnabled = {
  DEFAULT: boolean;
  POINT_TO_POINT: boolean;
  SNAP_TO_ROAD: boolean;
  DIRECTIONS_SERVICE: boolean;
};

// one order for both geometry and styling, so a single tick is unchanged
const MODE_PRECEDENCE: RouteRenderMode[] = [
  "POINT_TO_POINT",
  "SNAP_TO_ROAD",
  "DIRECTIONS_SERVICE",
  "DEFAULT",
];

const LINE_STYLE_BY_MODE: Record<RouteRenderMode, RouteLineStyle> = {
  POINT_TO_POINT: "dashed",
  SNAP_TO_ROAD: "solid_thin",
  DIRECTIONS_SERVICE: "dotted",
  DEFAULT: "solid",
};

export const lineStyleForMode = (mode: RouteRenderMode): RouteLineStyle =>
  LINE_STYLE_BY_MODE[mode] ?? "solid";

// every ticked view, in precedence order
export const enabledRouteModes = (
  routeViewsEnabled: RouteViewsEnabled | undefined
): RouteRenderMode[] => {
  if (!routeViewsEnabled) return ["DEFAULT"];
  const enabled = MODE_PRECEDENCE.filter((mode) => routeViewsEnabled[mode]);
  // nothing ticked would mean no route on the map at all, so default is the
  // floor - the same rule the checkboxes enforce
  return enabled.length > 0 ? enabled : ["DEFAULT"];
};

// the view the real line draws: it carries the event pins, the hover and
// click handling, the replay and the overlap badges
export const primaryRouteMode = (
  routeViewsEnabled: RouteViewsEnabled | undefined
): RouteRenderMode => enabledRouteModes(routeViewsEnabled)[0];

// the extra ticked views, drawn as static reference lines
export const overlayRouteModes = (
  routeViewsEnabled: RouteViewsEnabled | undefined
): RouteRenderMode[] => enabledRouteModes(routeViewsEnabled).slice(1);

const ALTERNATE_MODES: RouteRenderMode[] = [
  "POINT_TO_POINT",
  "SNAP_TO_ROAD",
  "DIRECTIONS_SERVICE",
];

const hasAnyAlternate = (views: RouteViewsEnabled) =>
  ALTERNATE_MODES.some((mode) => views[mode]);

// Ticking a view, with the rules both maps follow:
//   - the three alternates stack freely, that being the point of them
//   - default is exclusive: turning it on clears the alternates, and
//     turning an alternate on clears default
//   - something is always on. Nothing ticked would draw no route at all,
//     so default is the floor and comes back when the last alternate goes.
export const toggleRouteView = (
  views: RouteViewsEnabled,
  mode: RouteRenderMode
): RouteViewsEnabled => {
  if (mode === "DEFAULT") {
    // already the only thing on - the floor holds, nothing to do
    if (views.DEFAULT && !hasAnyAlternate(views)) return views;
    return {
      DEFAULT: true,
      POINT_TO_POINT: false,
      SNAP_TO_ROAD: false,
      DIRECTIONS_SERVICE: false,
    };
  }

  const next = { ...views, [mode]: !views[mode] };
  const stillHasAlternate = hasAnyAlternate(next);
  return { ...next, DEFAULT: !stillHasAlternate };
};

// whether a tick can come off at all - what the floor rule makes immovable,
// so the checkbox can be disabled rather than silently snapping back
export const routeViewIsLocked = (
  views: RouteViewsEnabled,
  mode: RouteRenderMode
): boolean =>
  mode === "DEFAULT" ? views.DEFAULT && !hasAnyAlternate(views) : false;
